import { useLocation } from 'react-router-dom'
import { shouldHideBackOnStoreTab } from '@/store/lib/storeBackNav'
import { StoreFooter } from '@/store/components/layout/StoreFooter'
import { StoreMobileHeader } from '@/store/components/layout/StoreMobileHeader'
import {
  HomePageSkeleton,
  OrdersListSkeleton,
  NotesPageSkeleton,
  PageHeaderSkeleton,
  ProductDetailPageSkeleton,
  ShopPageSkeleton,
  SearchPageSkeleton,
  CartPageSkeleton,
  StoreLegalPageSkeleton,
  StoreOrderDetailPageSkeleton,
  StoreSkeleton,
  WishlistPageSkeleton,
} from '@/store/components/StoreSkeletons'

function isAdminPath(pathname: string) {
  return pathname.startsWith('/admin')
}

function StorePageLoaderBody() {
  const { pathname, state } = useLocation()
  const withBack = !shouldHideBackOnStoreTab(pathname, state)

  if (pathname === '/' || pathname === '') {
    return <HomePageSkeleton />
  }

  if (/^\/shop\/[^/]+$/.test(pathname)) {
    return <ProductDetailPageSkeleton />
  }

  if (pathname === '/shop' || pathname.startsWith('/shop?')) {
    return (
      <div className="page-shell">
        <ShopPageSkeleton />
      </div>
    )
  }

  if (pathname === '/search') {
    return <SearchPageSkeleton withBack={false} />
  }

  if (pathname === '/notes') {
    return (
      <div className="page-shell">
        <PageHeaderSkeleton />
        <NotesPageSkeleton />
      </div>
    )
  }

  if (pathname === '/wishlist') {
    return (
      <div className="page-shell">
        <StoreSkeleton className="h-9 w-32 rounded-lg mb-8" />
        <WishlistPageSkeleton />
      </div>
    )
  }

  if (pathname === '/orders') {
    return (
      <div className="page-shell pb-6">
        <PageHeaderSkeleton withBack={false} />
        <div className="orders-lookup-hero mb-6 sm:mb-8">
          <div className="orders-lookup-hero-inner space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <StoreSkeleton className="h-11 brand-radius-a opacity-40" />
              <StoreSkeleton className="h-11 brand-radius-b opacity-40" />
            </div>
            <StoreSkeleton className="h-11 w-full brand-radius-a opacity-40" />
          </div>
        </div>
        <OrdersListSkeleton count={5} />
      </div>
    )
  }

  if (/^\/orders\/[^/]+$/.test(pathname)) {
    return <StoreOrderDetailPageSkeleton />
  }

  if (
    pathname === '/privacy-policy' ||
    pathname === '/terms-and-conditions' ||
    pathname === '/return-refund-policy' ||
    pathname === '/about-us' ||
    pathname === '/shipping-policy'
  ) {
    return (
      <div className="page-shell md:pb-14">
        <div className="glass-card gold-border-glow p-6 md:p-10 max-w-3xl mx-auto">
          <div className="flex items-start gap-4 mb-8">
            <StoreSkeleton className="h-12 w-12 shrink-0 rounded-xl" />
            <div className="space-y-2 flex-1">
              <StoreSkeleton className="h-8 w-48 rounded-lg" />
              <StoreSkeleton className="h-4 w-32 rounded-md" />
            </div>
          </div>
          <StoreLegalPageSkeleton />
        </div>
      </div>
    )
  }

  if (pathname === '/cart') {
    return <CartPageSkeleton withBack={withBack} />
  }

  if (pathname === '/contact') {
    return (
      <div className="store-container pb-10 md:pb-14 max-w-2xl">
        <StoreSkeleton className="h-9 w-40 rounded-lg mb-2" />
        <StoreSkeleton className="h-4 w-full max-w-md rounded-md mb-8" />
        <div className="glass-card gold-border-glow p-6 md:p-8 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <StoreSkeleton key={i} className="h-10 w-full rounded-xl" />
          ))}
          <StoreSkeleton className="h-28 w-full rounded-xl" />
          <StoreSkeleton className="h-11 w-32 rounded-xl" />
        </div>
      </div>
    )
  }

  return <HomePageSkeleton />
}

/** Route-matched skeleton while a lazy store page chunk loads (inside StoreLayout). */
export function StoreRouteFallback() {
  return <StorePageLoaderBody />
}

/** Storefront shell + route-matched skeleton for initial load and lazy route chunks. */
export function StorePageLoader() {
  return (
    <div className="flex min-h-screen w-full max-w-[100vw] flex-col overflow-x-clip">
      <StoreMobileHeader />
      <main className="store-main flex-1">
        <StorePageLoaderBody />
      </main>
      <StoreFooter />
    </div>
  )
}

export function resolveStorePageLoader(pathname: string) {
  return !isAdminPath(pathname)
}
